import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useDebounce } from '@/shared/hooks/useDebounce';
import type { AsyncComboboxOption, AsyncComboboxFetchFn } from './AsyncCombobox.types';
import type { DataResponse } from '@/shared/hooks/useDataSource.types';

interface UseInfiniteSelectOptions<T extends AsyncComboboxOption> {
  queryKey: readonly unknown[];
  fetchFn?: AsyncComboboxFetchFn<T>;
  pageSize: number;
  enabled: boolean;
  staleTime: number;
  debounceMs: number;
}

export function useInfiniteSelect<T extends AsyncComboboxOption>({
  queryKey,
  fetchFn,
  pageSize,
  enabled,
  staleTime,
  debounceMs,
}: UseInfiniteSelectOptions<T>) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, debounceMs);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [...queryKey, { search: debouncedSearch, pageSize }],
    queryFn: async ({ pageParam }): Promise<DataResponse<T>> => {
      if (fetchFn) {
        return fetchFn({
          search: debouncedSearch,
          cursor: pageParam as string | undefined,
          pageSize,
        });
      }
      return { items: [] };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && !!fetchFn,
    staleTime,
  });

  const options = data?.pages.flatMap((p) => p.items) ?? [];

  return {
    options,
    isLoading,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    search,
    setSearch,
  };
}
